/**
 * Commands, events and the result type that joins them.
 *
 * Every change to the game state is a command. The player issues them by clicking;
 * the AI will issue exactly the same ones from Phase 5. One validated entry point
 * means one place where the rules live, a free notification stream, and — because a
 * command is plain data — replayable games at no extra cost.
 */

import type { DealItem, Id, ProductionItem, UnitOrder } from '../state/types.js';

export type Command =
  /** Move one unit to ONE ADJACENT tile. Distance is covered by goto orders, not by this. */
  | { t: 'MoveUnit'; unitId: Id; to: number }
  /** Path to a tile and store the route as a standing order. */
  | { t: 'SetGoto'; unitId: Id; to: number }
  | { t: 'CancelOrders'; unitId: Id }
  | { t: 'Fortify'; unitId: Id }
  | { t: 'Sleep'; unitId: Id }
  /** Give up the rest of this unit's turn without giving it a standing order. */
  | { t: 'SkipUnit'; unitId: Id }
  /** Hand a worker to automation: it chooses its own jobs each turn (ADR-0077). */
  | { t: 'Automate'; unitId: Id }
  /** Spend a settler to found a city where it stands. */
  | { t: 'FoundCity'; unitId: Id }
  /** Replace a city's queue with a single item. */
  | { t: 'SetProduction'; cityId: Id; item: ProductionItem }
  /** Append to a city's queue. */
  | { t: 'QueueItem'; cityId: Id; item: ProductionItem }
  /** Remove the entry at a position in the queue. */
  | { t: 'DequeueItem'; cityId: Id; position: number }
  /** Move a queue entry to another position, so a plan can be reordered rather than rebuilt. */
  | { t: 'MoveQueueItem'; cityId: Id; from: number; to: number }
  /** Set a worker building an improvement where it stands. */
  | { t: 'BuildImprovement'; unitId: Id; improvement: Id }
  /** Work or stop working a tile by hand, which takes the city off its governor. */
  | { t: 'ToggleWorkedTile'; cityId: Id; tile: number }
  /** Hand the city back to the governor. */
  | { t: 'AutoArrange'; cityId: Id }
  /** What citizens with no tile do: a specialist id, or -1 for Auto. */
  | { t: 'SetSpecialist'; cityId: Id; specialist: number }
  /** Research this technology now. Its prerequisites must all be known. Clears any goal. */
  | { t: 'SetResearch'; tech: Id }
  /** Aim at any unknown technology; the way there is researched step by step. */
  | { t: 'SetResearchGoal'; tech: Id }
  /** The share of commerce sent to research, in percent. */
  | { t: 'SetScienceRate'; rate: number }
  /** Go to war with another civilisation. Attacking one at peace needs this first. */
  | { t: 'DeclareWar'; on: Id }
  /** Offer peace to a civilisation at war with you. An AI answers at once; a player later. */
  /** Offer peace; `give` and `get` are terms, seen from the one offering (ADR-0071). */
  | { t: 'ProposePeace'; to: Id; give?: DealItem[]; get?: DealItem[] }
  /** Go aboard one of your ships on the same tile (in port). Stepping onto a ship at sea boards it too. */
  | { t: 'Board'; unitId: Id }
  /** Leave the ship, staying on its tile — only in port. At sea, step onto land instead. */
  | { t: 'Unload'; unitId: Id }
  /** Rebuild a unit as the newest version its owner can build, for gold, in one of its cities. */
  | { t: 'UpgradeUnit'; unitId: Id }
  /** Fire a unit's guns at an enemy within its bombard range (ADR-0064). */
  | { t: 'Bombard'; unitId: Id; target: number }
  /** An aircraft's mission (ADR-0065): strike an enemy within range, or rebase to one of your cities. */
  | { t: 'AirMission'; unitId: Id; target: number }
  /** Have a ship follow and guard another of your ships (ADR-0067). */
  | { t: 'Escort'; unitId: Id; target: Id }
  /** Answer a peace offer another civilisation has made you. */
  | { t: 'AnswerPeace'; from: Id; accept: boolean }
  /** Begin a revolution towards another government (ADR-0059). */
  | { t: 'ChangeGovernment'; government: Id }
  /** Offer a deal (ADR-0060): what you give, what you ask. A demand gives nothing and asks under threat. */
  | { t: 'ProposeDeal'; to: Id; give: DealItem[]; get: DealItem[]; demand?: boolean }
  /** Answer a deal offered to you. */
  | { t: 'AnswerOffer'; offerId: Id; accept: boolean }
  /** End an agreement: stop a payment (yours: a default; theirs to you: a kindness), or close borders. */
  | { t: 'CancelAgreement'; payment?: Id; openBordersWith?: Id }
  | { t: 'EndTurn'; civId: Id };

export type CommandKind = Command['t'];

/**
 * Why a command was refused.
 *
 * Codes rather than sentences: the interface turns them into words, so a rule and
 * its explanation cannot drift apart, and the same code can grey out a button.
 */
export type CommandError =
  | 'NoSuchUnit'
  | 'NotYourUnit'
  | 'NotYourTurn'
  | 'NotAdjacent'
  | 'NoMovesLeft'
  | 'Impassable'
  | 'WrongDomain'
  | 'OffMap'
  | 'NoPath'
  | 'AlreadyThere'
  | 'CannotFound'
  | 'CityHere'
  | 'CityTooClose'
  | 'NotHabitable'
  | 'NoSuchCity'
  | 'NotYourCity'
  | 'NoSuchItem'
  | 'AlreadyBuilt'
  | 'AlreadyQueued'
  | 'NothingQueued'
  | 'TileNotWorkable'
  | 'NoSpareCitizen'
  | 'CannotBuild'
  | 'WrongGround'
  | 'AlreadyImproved'
  | 'NoSuchTech'
  | 'AlreadyKnown'
  | 'TechLocked'
  | 'BadRate'
  | 'NeedsTech'
  | 'Obsolete'
  | 'NoSuchSpecialist'
  | 'AtPeace'
  | 'CannotAttack'
  | 'CannotCapture'
  | 'NoSuchCiv'
  | 'AlreadyAtWar'
  | 'NotAtWar'
  | 'Truce'
  | 'OfferTooSoon'
  | 'NoOffer'
  | 'Occupied'
  | 'NoShip'
  | 'FromShip'
  | 'NotAboard'
  | 'AtSea'
  | 'ZoneOfControl'
  | 'NoUpgrade'
  | 'NotInCity'
  | 'CannotAfford'
  | 'ForeignTerritory'
  | 'NotBuildable'
  | 'OtherCivsUnit'
  | 'NeedsResource'
  | 'CannotBombard'
  | 'OutOfRange'
  | 'NothingToBombard'
  | 'AircraftFly'
  | 'NotAircraft'
  | 'NothingToStrike'
  | 'CannotEscort'
  | 'NoSuchGovernment'
  | 'AlreadyGovernment'
  | 'InRevolution'
  | 'RateAboveGovernment'
  | 'NotMet'
  | 'AtWarWith'
  | 'EmptyDeal'
  | 'BadDealItem'
  | 'ClosedBorders'
  | 'NoSuchAgreement'
  | 'OfferStale'
  | 'WonderBuilt'
  | 'WonderUnderway'
  | 'NotCoastal'
  | 'AlreadyWorking';

export const ERROR_TEXT: Record<CommandError, string> = {
  NoSuchUnit: 'That unit no longer exists.',
  NotYourUnit: 'That unit belongs to someone else.',
  NotYourTurn: 'It is not your turn.',
  NotAdjacent: 'A unit can only step to a neighbouring tile.',
  NoMovesLeft: 'That unit has no movement left this turn.',
  Impassable: 'Nothing can cross that.',
  WrongDomain: 'That unit cannot travel there.',
  OffMap: 'That is not a tile.',
  NoPath: 'There is no known route there.',
  AlreadyThere: 'The unit is already there.',
  CannotFound: 'That unit cannot found a city.',
  CityHere: 'There is already a city on this tile.',
  CityTooClose: 'Another city is too close.',
  NoSuchGovernment: 'There is no such government.',
  AlreadyGovernment: 'That is already your government.',
  InRevolution: 'A revolution is already under way.',
  NotMet: 'You have not met them.',
  AtWarWith: 'You are at war with them — make peace first.',
  EmptyDeal: 'A deal needs something in it.',
  BadDealItem: 'Something in that deal cannot be given.',
  ClosedBorders: 'Their border is closed to your units. Agree open borders, or go to war.',
  NoSuchAgreement: 'There is no such agreement.',
  OfferStale: 'That offer can no longer be carried out.',
  RateAboveGovernment: 'Your government does not allow that much science.',
  OtherCivsUnit: 'Your civilisation builds its own version of that unit.',
  NeedsResource: 'This city lacks a strategic resource that needs — join one to it by road or river, or trade for one.',
  CannotBombard: 'That unit has no guns that reach beyond its own tile.',
  OutOfRange: 'That is beyond this unit\'s range.',
  NothingToBombard: 'There is no enemy there that bombardment could still hurt.',
  AircraftFly: 'Aircraft fly missions from their city: right-click an enemy in range to strike it, or one of your cities to rebase.',
  NotAircraft: 'Only aircraft fly missions.',
  NothingToStrike: 'There is nothing there an air strike could hit — and aircraft never take a city.',
  CannotEscort: 'A warship can escort another of your ships — not itself, and not a ship that is escorting it.',
  NotBuildable: 'That cannot be built — a civilisation has one palace, in its first city.',
  ForeignTerritory: 'This ground lies inside another civilisation\'s border.',
  WonderBuilt: 'That wonder has already been built — there is only one in the world.',
  WonderUnderway: 'Another of your cities is already building that wonder.',
  NotHabitable: 'A city cannot be built on that ground.',
  NoSuchCity: 'That city no longer exists.',
  NotYourCity: 'That city belongs to someone else.',
  NoSuchItem: 'That cannot be built.',
  AlreadyBuilt: 'This city has already built that.',
  AlreadyQueued: 'That is already in the queue.',
  NothingQueued: 'There is nothing there to remove.',
  TileNotWorkable: 'This city cannot work that tile.',
  NoSpareCitizen: 'Every citizen is already working a tile.',
  CannotBuild: 'That unit cannot build anything.',
  WrongGround: 'That cannot be built on this ground.',
  AlreadyImproved: 'That is already here.',
  NoSuchTech: 'There is no such technology.',
  AlreadyKnown: 'Your scholars already know that.',
  TechLocked: 'Something it depends on has not been discovered yet.',
  BadRate: 'The science rate cannot be set to that.',
  NeedsTech: 'Your people do not yet know how to build that.',
  Obsolete: 'That has been superseded by something better.',
  NoSuchSpecialist: 'There is no such job for a citizen.',
  AtPeace: 'You are at peace with them. Declare war first.',
  CannotAttack: 'That unit cannot attack.',
  CannotCapture: 'Only a unit that can fight can take that.',
  NoSuchCiv: 'There is no such civilisation.',
  AlreadyAtWar: 'You are already at war with them.',
  NotAtWar: 'You are not at war with them.',
  Truce: 'A truce still holds — you cannot declare war yet.',
  OfferTooSoon: 'You offered peace too recently to ask again.',
  NoOffer: 'They have not offered peace.',
  Occupied: 'Someone else is standing there.',
  NoShip: 'There is no ship of yours there with room to carry it.',
  FromShip: 'Units cannot attack straight off a ship — land first.',
  NotAboard: 'That unit is not aboard a ship.',
  AtSea: 'Not at sea — step onto land to go ashore.',
  ZoneOfControl: 'An enemy soldier is next to both tiles — stop, go round, or attack it.',
  NoUpgrade: 'There is nothing better your people can build to replace it.',
  NotInCity: 'Only a unit in one of your cities can do that.',
  CannotAfford: 'The treasury cannot pay for that.',
  NotCoastal: 'Only a city beside the sea can build ships.',
  AlreadyWorking: 'That is already being built here.',
};

export type GameEvent =
  | { t: 'UnitMoved'; unitId: Id; from: number; to: number; cost: number }
  | { t: 'UnitOrderSet'; unitId: Id; order: UnitOrder }
  | { t: 'UnitOrderCleared'; unitId: Id }
  | { t: 'UnitOrderCompleted'; unitId: Id }
  | { t: 'GotoBlocked'; unitId: Id; at: number }
  | { t: 'TilesDiscovered'; civId: Id; count: number }
  | { t: 'CityFounded'; cityId: Id; civId: Id; name: string; at: number }
  | { t: 'CityGrew'; cityId: Id; population: number }
  | { t: 'CityStarved'; cityId: Id; population: number }
  | { t: 'BuildingCompleted'; cityId: Id; building: Id; name: string }
  | { t: 'WonderCompleted'; cityId: Id; civId: Id; building: Id; name: string; cityName: string }
  /** A city's wonder was finished elsewhere first; `stored` is production carried over, if it was the head. */
  | { t: 'WonderLost'; cityId: Id; name: string; by: Id; stored: number }
  | { t: 'BordersExpanded'; cityId: Id; name: string; radius: number }
  | { t: 'RevolutionStarted'; civId: Id; target: Id; name: string; turns: number }
  | { t: 'GovernmentChanged'; civId: Id; government: Id; name: string }
  | { t: 'AllianceEnded'; a: Id; b: Id; against: Id }
  | { t: 'AllianceMade'; a: Id; b: Id; against: Id }
  | { t: 'TreatyBroken'; by: Id; with: Id }
  | { t: 'DealMade'; from: Id; to: Id; give: DealItem[]; get: DealItem[]; demand: boolean }
  | { t: 'DealRefused'; by: Id; to: Id; reason: string; demand: boolean }
  | { t: 'DealOffered'; offerId: Id; from: Id; to: Id; demand: boolean }
  | { t: 'OfferWithdrawn'; offerId: Id; from: Id; to: Id }
  | { t: 'PaymentStopped'; from: Id; to: Id; defaulted: boolean }
  /** A strategic resource deal ended: its term ran out, or (lost) the giver no longer had it. */
  | { t: 'ResourceGrantEnded'; from: Id; to: Id; resource: number; lost: boolean }
  | { t: 'OpenBordersEnded'; by: Id; with: Id }
  | { t: 'UnitsExpelled'; civId: Id; from: Id; count: number }
  | { t: 'GameWon'; winner: Id; kind: 'conquest'; turn: number }
  | { t: 'UnitCompleted'; cityId: Id; unitType: Id; name: string }
  | { t: 'ProductionHeld'; cityId: Id; item: string }
  /** A unit at the head of the queue waits for a strategic resource the city lacks (ADR-0063). */
  | { t: 'ProductionNeedsResource'; cityId: Id; item: string; resource: string }
  | { t: 'CityInDisorder'; cityId: Id; name: string }
  | { t: 'BuildingSold'; cityId: Id; name: string; cityName: string }
  | { t: 'Bankrupt'; civId: Id }
  | { t: 'ImprovementBuilt'; at: number; name: string; civId: Id }
  | { t: 'WorkAbandoned'; unitId: Id; at: number }
  | { t: 'ProductionSet'; cityId: Id; name: string }
  | { t: 'ProductionLost'; cityId: Id; amount: number }
  | { t: 'WorkedTilesChanged'; cityId: Id }
  | { t: 'TechDiscovered'; civId: Id; tech: Id; name: string }
  | { t: 'ResearchSet'; civId: Id; tech: Id; goal: Id }
  | { t: 'ScienceRateSet'; civId: Id; rate: number }
  | { t: 'EraEntered'; civId: Id; era: number; name: string }
  | {
      t: 'CombatResolved';
      attackerId: Id;
      defenderId: Id;
      attackerType: Id;
      defenderType: Id;
      attackerOwner: Id;
      defenderOwner: Id;
      at: number;
      attackerWon: boolean;
      attackerHp: number;
      defenderHp: number;
      /** The odds the attacker was shown, 0–1. */
      odds: number;
    }
  | { t: 'UnitCaptured'; unitId: Id; unitType: Id; from: Id; to: Id }
  | { t: 'UnitBoarded'; unitId: Id; shipId: Id; at: number }
  | { t: 'UnitLanded'; unitId: Id; shipId: Id; at: number }
  /** Units lost with a ship that went down. */
  | { t: 'CargoLost'; shipId: Id; owner: Id; count: number }
  | { t: 'UnitPromoted'; unitId: Id; owner: Id; rank: string }
  /** An enemy fighter met a strike (ADR-0065): health left on both sides afterwards. */
  | { t: 'AirIntercepted'; strikerId: Id; strikerType: Id; strikerOwner: Id; interceptorId: Id; interceptorOwner: Id; at: number; strikerHp: number; interceptorHp: number }
  /** A city's anti-aircraft fired at a strike. */
  | { t: 'AntiAirFired'; strikerId: Id; strikerOwner: Id; cityOwner: Id; at: number; hits: number; damage: number }
  /** An aircraft lost: shot down, or destroyed on the ground when its city fell. */
  | { t: 'AircraftLost'; unitId: Id; unitType: Id; owner: Id; at: number; cause: 'intercepted' | 'antiAir' | 'shotDown' | 'cityFell' }
  | { t: 'AircraftRebased'; unitId: Id; owner: Id; from: number; to: number }
  | { t: 'EscortSet'; unitId: Id; target: Id }
  /** A bombardment (ADR-0064): shots fired, hits landed, health taken. Nobody dies of it. */
  | {
      t: 'UnitBombarded';
      attackerId: Id;
      targetId: Id;
      attackerOwner: Id;
      targetOwner: Id;
      targetType: Id;
      at: number;
      shots: number;
      hits: number;
      damage: number;
      targetHp: number;
    }
  | { t: 'UnitUpgraded'; unitId: Id; from: Id; to: Id; cost: number }
  | { t: 'CityCaptured'; cityId: Id; name: string; from: Id; to: Id; lost: string[]; citizensLost: number }
  | { t: 'CivDefeated'; civId: Id; name: string }
  | { t: 'WarDeclared'; by: Id; on: Id }
  | { t: 'PeaceMade'; a: Id; b: Id }
  | { t: 'PeaceOffered'; from: Id; to: Id; give: DealItem[]; get: DealItem[] }
  /** What changed hands to end a war: `from` gave `give` and received `get` (ADR-0071). */
  | { t: 'PeaceTerms'; from: Id; to: Id; give: DealItem[]; get: DealItem[] }
  | { t: 'CityCeded'; cityId: Id; name: string; from: Id; to: Id }
  | { t: 'PeaceRefused'; by: Id; to: Id }
  | { t: 'TurnEnded'; turn: number; civId: Id }
  | { t: 'TurnBegan'; turn: number; civId: Id };

export type Result<T> = { ok: true; value: T } | { ok: false; error: CommandError };

export function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

export function fail<T>(error: CommandError): Result<T> {
  return { ok: false, error };
}
